import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import { documentInvoiceService } from '../../services/documentInvoiceService';
import { Invoice } from '../../types/payment';

interface InvoicePDFPreviewProps {
  invoice: Invoice;
  onDownloadPDF: (template: string) => void;
}

const InvoicePDFPreview: React.FC<InvoicePDFPreviewProps> = ({
  invoice,
  onDownloadPDF
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [template, setTemplate] = useState<string>('tally_prime');

  const generatePDFPreview = async (selectedTemplate: string = template) => {
    try {
      setLoading(true);
      setError(null);
      
      const blob = await documentInvoiceService.downloadInvoicePDF(invoice.id, selectedTemplate);
      const url = window.URL.createObjectURL(blob);
      
      // Revoke old URL if exists
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
      
      setPdfUrl(url);
    } catch (error: any) {
      console.error('Failed to generate PDF preview:', error);
      setError(error.response?.data?.detail || 'Failed to generate PDF preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generatePDFPreview();
    
    // Cleanup URL when component unmounts
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [invoice.id]);

  const handleRefresh = () => {
    generatePDFPreview();
  };

  const handleTemplateChange = (newTemplate: string) => {
    setTemplate(newTemplate);
    generatePDFPreview(newTemplate);
  };

  const handleFullscreen = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleDownload = () => {
    onDownloadPDF(template);
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Typography variant="h6">
            PDF Preview
          </Typography>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Template</InputLabel>
              <Select
                value={template}
                onChange={(e) => handleTemplateChange(e.target.value)}
                label="Template"
                disabled={loading}
              >
                <MenuItem value="tally_prime">Tally Prime</MenuItem>
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="thermal">Thermal</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh Preview">
              <span>
                <IconButton onClick={handleRefresh} disabled={loading} size="small">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Open in New Tab">
              <span>
                <IconButton onClick={handleFullscreen} disabled={!pdfUrl || loading} size="small">
                  <FullscreenIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={loading}
              size="small"
            >
              Download
            </Button>
          </Box>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={handleRefresh} size="small" sx={{ ml: 1 }}>
              Retry
            </Button>
          </Alert>
        )}

        {pdfUrl && !loading && (
          <Box
            sx={{
              width: '100%',
              height: 600,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            <iframe
              src={pdfUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={`Invoice ${invoice.invoice_number} PDF Preview`}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoicePDFPreview;
